import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { SET_ON_CONTINUE, SET_LOADING } from '../../reducers/actionReducer';
import { currency } from '../../utils';
import { getProduct } from '../../helpers/products';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_ENHANCEMENT_PACKAGES_QUERY } from '../../queries/packageQueries';
import Button from '../inputs/Button';

type EnhancementVariable = string | { name: string; dataset?: string };

type DisplayEnhancementPackageProps = {
  packageName: string;
  productKey?: string;
  rate: number;
  descriptionLong: string;
  variablesIncluded: EnhancementVariable[];
  additionalDescription?: JSX.Element;
  componentType: string;
  matchedEnhancement?: any;
  dispatchAction: any;
  openOrder?: any;
};

export default function DisplayEnhancementPackage({
  packageName,
  productKey,
  descriptionLong,
  variablesIncluded,
  additionalDescription,
  componentType,
  matchedEnhancement,
  dispatchAction,
  rate,
  openOrder,
}: DisplayEnhancementPackageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const numMatchedRecords = matchedEnhancement?.numMatchedRecords
    ? parseFloat(matchedEnhancement?.numMatchedRecords)
    : null;
  const { fileId } = useParams();

  const audienceData = location.state?.orderDetails;
  const [expanded, setExpanded] = useState(false);
  const MAX_VISIBLE = 20;

  // Check if we should show two buttons (no context)
  const showTwoButtons = !matchedEnhancement && !audienceData?.audienceId;

  // Fetch default enhancement packages from backend
  const { data: defaultPkgsData } = useQuery(DEFAULT_ENHANCEMENT_PACKAGES_QUERY, {
    fetchPolicy: 'cache-first',
  });

  // Create alias map for package name normalization
  const aliasMap = { Foundation: 'Foundations' };

  // Merge backend variables with received props
  const backendPackageName = aliasMap[packageName] || packageName;
  const backendPkg = defaultPkgsData?.defaultEnhancementPackages?.find(
    (p: any) => p?.packageName === backendPackageName
  );
  const effectiveVariables = backendPkg?.variablesIncluded ?? variablesIncluded;

  useEffect(() => {
    if (dispatchAction) {
      dispatchAction({
        type: SET_ON_CONTINUE,
        payload: {
          onContinue: async () => {
            try {
              // Set loading state
              dispatchAction({
                type: SET_LOADING,
                payload: { loading: true },
              });

              if (matchedEnhancement) {
                // File enhancement flow
                navigate('../../order', {
                  state: {
                    orderDetails: {
                      coreProductName: productKey,
                      fileId,
                      fileName: matchedEnhancement.name,
                      packageName,
                      matchedRecords: numMatchedRecords,
                    },
                  },
                });
              } else if (audienceData?.audienceId && openOrder) {
                // Audience enhancement flow - create order and navigate to summary
                // Get the product for audience enhancement
                const product = getProduct('audience-enhancement', user?.plan);

                // Create the order
                const result = await openOrder({
                  variables: {
                    products: [{ ...product, quantity: audienceData.recordsIncluded }],
                    orderType: 'audience-enhancement',
                    orderDetails: {
                      audience: audienceData.audienceId,
                      audienceName: audienceData.audienceName,
                      packageName,
                      recordsIncluded: audienceData.recordsIncluded,
                    },
                  },
                });

                // Navigate to order summary with the new order ID
                const orderId = result.data?.openOrder?.id;
                if (orderId) {
                  navigate(`./${orderId}`, {
                    state: {
                      orderDetails: {
                        orderId,
                        audienceId: audienceData.audienceId,
                        audienceName: audienceData.audienceName,
                        packageName,
                        recordsIncluded: audienceData.recordsIncluded,
                        coreProductName: 'audience-enhancement',
                      },
                    },
                  });
                } else {
                  console.error('Failed to create order: no order ID returned');
                }
              } else {
                // Default to file selection when no two-button mode
                if (!showTwoButtons) {
                  navigate('../fileselection');
                }
              }
            } catch (error) {
              console.error('Error creating enhancement order:', error);
              alert('Failed to create order. Please try again.');
            } finally {
              // Clear loading state
              dispatchAction({
                type: SET_LOADING,
                payload: { loading: false },
              });
            }
          },
        },
      });
    }
  }, [
    dispatchAction,
    fileId,
    matchedEnhancement,
    navigate,
    openOrder,
    productKey,
    packageName,
    rate,
    numMatchedRecords,
    audienceData,
    user?.plan,
    showTwoButtons,
  ]);

  const isArray = Array.isArray(effectiveVariables);
  const showToggle = isArray && (effectiveVariables as EnhancementVariable[]).length > MAX_VISIBLE;
  const itemsToRender = isArray
    ? (expanded
        ? (effectiveVariables as EnhancementVariable[])
        : (effectiveVariables as EnhancementVariable[]).slice(0, MAX_VISIBLE))
    : [];

  return (
    <div className="w-full h-full flex flex-col pb-10 px-3">
      {(!matchedEnhancement || !effectiveVariables) && (
        <p className="mb-6">{descriptionLong}</p>
      )}
      <div className="flex justify-center">
        {effectiveVariables && (
          <>
            <div className="w-4/5">
              <p className="font-bold mb-2 mt-4">
                {componentType === 'identifiers'
                  ? 'Identifiers Included:'
                  : 'Elements Include:'}
              </p>

              {typeof effectiveVariables === 'string' && (
                <p>{effectiveVariables}</p>
              )}
              {isArray && (
                <div className="pl-5 mb-6">
                  <ul className="list-disc">
                    {itemsToRender.map((variable) => (
                      <li key={typeof variable === 'string' ? variable : JSON.stringify(variable)}>
                        {typeof variable === 'string' ? variable : (variable?.name || '')}
                      </li>
                    ))}
                  </ul>
                  {showToggle && (
                    <button
                      type="button"
                      className="mt-2 text-blue-600 hover:text-primary underline"
                      onClick={() => setExpanded((v) => !v)}
                    >
                      {expanded
                        ? 'See less'
                        : `See all (${(effectiveVariables as EnhancementVariable[]).length - MAX_VISIBLE} more)`}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {additionalDescription && <>{additionalDescription}</>}
      {matchedEnhancement && (
        <div className="w-full flex flex-col flex-grow items-center justify-end font-bold mt-10">
          <div className="flex flex-col space-y-2">
            <p>
              <span className="mr-2">Matched Records:</span>{' '}
              {numMatchedRecords.toLocaleString()}
            </p>
            <p>
              <span className="mr-2">Price per record:</span>{' '}
              {currency(rate, 2, 4)}
            </p>
            <p>
              <span className="mr-2">Enhancement Price:</span>{' '}
              {currency(parseFloat(String(rate)) * numMatchedRecords)}
            </p>
          </div>
        </div>
      )}
      {showTwoButtons && (
        <div className="flex flex-col items-center mt-10">
          <div className="flex gap-3 justify-center">
            <Button
              type="button"
              secondary
              onClick={() => navigate('/dashboard/mywork/orders')}
            >
              View My Orders
            </Button>
            <Button
              type="button"
              primary
              onClick={() => navigate('../fileselection')}
            >
              Upload File
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
